/**
 * src/lib/forms/iletisim-schema.ts
 *
 * Iletisim formu Zod schema. Hem client (RHF + zodResolver) hem Phase β'da
 * server action / route handler tarafindan validate icin paylasilir
 * (single source of truth — boundary'de tekrar parse edilir).
 *
 * Validation mesajlari Zod tarafinda placeholder ('forms.iletisim.validation.X')
 * olarak verilir; client tarafinda next-intl `useTranslations('forms.iletisim')`
 * ile cevirilir. Sebep: schema framework-bagimsiz oldugu icin t() cagrisi
 * yapamaz; mesaj key'i string olarak tasir.
 *
 * Pure TS — framework import YOK (zod stdlib helper). i18n-conventions.md
 * gereginden boundary validate kurali burada.
 */

import { z } from 'zod'

/**
 * Iletisim formu konu (subject) secenekleri. value field URL/DB icin stabil
 * snake_case; label client'ta i18n key veya domain'e gore turevsel cevirilir.
 *
 * Phase β'da yeni konu eklemek icin: bu listeye ekle + messages dosyalarinda
 * 'forms.iletisim.konu_options.<value>' key'i ekle.
 */
export const ILETISIM_KONU_OPTIONS = [
  'sizma-testi',
  'iso-danismanlik',
  'soc-hizmeti',
  'diger',
] as const

export type IletisimKonu = (typeof ILETISIM_KONU_OPTIONS)[number]

/**
 * iletisimFormSchema — RHF + zodResolver ile kullanilan validation.
 *
 * Field davranisi:
 *   - adSoyad: min 2 char (ad+soyad ayri olmasi sart degil; "Ahmet" yeterli)
 *   - email: standart email format
 *   - telefon: opsiyonel, bos string veya 7+ rakam/format icerebilir
 *   - konu: ILETISIM_KONU_OPTIONS'tan biri (literal union)
 *   - mesaj: min 20 char (spam azaltici)
 *   - kvkkConsent: true zorunlu (false fail)
 *
 * Error message string'leri t() key'i; client component duzeyinde cevirilir.
 */
export const iletisimFormSchema = z.object({
  adSoyad: z
    .string()
    .min(2, { message: 'forms.iletisim.validation.ad_soyad_required' })
    .max(100),
  email: z
    .string()
    .min(1, { message: 'forms.iletisim.validation.email_required' })
    .email({ message: 'forms.iletisim.validation.email_invalid' })
    .max(254),
  telefon: z.string().max(30).optional().or(z.literal('')),
  konu: z.enum(ILETISIM_KONU_OPTIONS, {
    message: 'forms.iletisim.validation.konu_required',
  }),
  mesaj: z
    .string()
    .min(1, { message: 'forms.iletisim.validation.mesaj_required' })
    .min(20, { message: 'forms.iletisim.validation.mesaj_min' })
    .max(2000),
  kvkkConsent: z.literal(true, {
    message: 'forms.iletisim.validation.kvkk_required',
  }),
})

/**
 * IletisimFormValues — schema'dan inferred type. RHF generic'i icin
 * `useForm<IletisimFormValues>()`.
 */
export type IletisimFormValues = z.infer<typeof iletisimFormSchema>
